import loadjs from "../loadjs";
import { IconMapChunkType, IconMapType, LoadOptionsType, LoadParamsType } from "../../types";
import {
  appendSymbolToRefs, eventEmit, getIconLowerKeysMap, getIconMap, getListState, getSymbolArea, setIconLowerKeysMap,
  setIconMap, setSymbolArea, svgElement, isIconMapLoaded, isIconLowerKeysMapLoaded,
} from "../IconLoader";
import { ICONAMP_BUNDLEID, ICONCHUNK_PATH_PREFIX, ICONMAP_PATH } from "../../constant";
import { toIconNameLowerMap } from "..";
import { AnyObj } from "../../../../types/common";
import { eventEmitter, ua } from "@weapp/utils";
import { uiAppName } from "../../../../constants";

let resourceDomain: string | null = null;

/** 图标库版本 */
let iconVersion: string | null = null;


let systemConfigPromise: Promise<{
  iconResourceDomainCache: string;
  iconVersionCache: string | null;
}> | null = null;


/** icon chunk 缓存记录 */
let iconChunkMap: {
  [key: string]: AnyObj
} = {};

/**
 * 加载 script 文件获取 json 内容
 * @param params LoadParamsType
 * @param options LoadOptionsType
 * @returns {Promise<any>}
 */
export const loadJSONJS = (params: LoadParamsType, options?: Partial<LoadOptionsType>): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    let sourcePath = params.domain + params.path;

    const onSuccess = (json: any) => {
      resolve(json)
    };

    const onError = (err: any) => {
      reject(`Load ${params.path} error: ${err}`);
    }

    loadjs.isDefined(params.bundleId)
      ? loadjs.ready(params.bundleId, {
        ...options,
        success: onSuccess,
        error: onError,
      })
      : loadjs(
        [
          sourcePath
        ],
        params.bundleId,
        {
          ...options,
          success: onSuccess,
          error: onError,
          async: true,
        }
      )
  })
}

export const getIconResourceDomain = async (domain?: string) => {
  if (!systemConfigPromise) {
    systemConfigPromise = import(
      /* webpackChunkName: 'icon' */
      '../getIconResourceDomainPromise'
    ).then((defaultModule) => {
      return defaultModule?.getSystemConfigPromise();
    });
  }
  return systemConfigPromise;

}

export const loadIconMap = async (params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>): Promise<IconMapType> => {
  const isIML = isIconMapLoaded(), isILKML = isIconLowerKeysMapLoaded();
  try {
    if (!isIML) {
      if (resourceDomain === null) {
        const { iconResourceDomainCache, iconVersionCache } = await getIconResourceDomain();
        resourceDomain = params?.domain || iconResourceDomainCache;
        iconVersion = iconVersionCache;
      }

      const iconMapRes = await loadJSONJS({
        domain: resourceDomain || '',
        path: params?.path || ICONMAP_PATH,
        bundleId: params?.bundleId || ICONAMP_BUNDLEID,
      }, {
        noCache: iconVersion ? false : true,
        version: iconVersion,
        ...options
      });

      setIconMap(iconMapRes);
    }
    const iconMap = getIconMap();
    if (iconMap) {
      if (!isILKML) {
        setIconLowerKeysMap(toIconNameLowerMap(iconMap))
      }
      return Promise.resolve(iconMap);
    } else {
      return Promise.reject();
    }
  } catch (error) {
    console.warn(error);
    return Promise.reject(error);
  }
}

export const getIconChunk = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>): Promise<string> => {
  const isIML = isIconMapLoaded()
  try {
    if (!isIML) {
      await loadIconMap(params, options);
    }

    const iconMap = getIconMap();

    let realIconName = iconName;
    let bunildId = '', path = '';
    const verinfo = iconMap?.['version']?.toString() || 0;

    if (Number(verinfo) >= 2) {
      realIconName = iconName.replace(/^[Ii]con-/, '');
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      const chunkKey = iconMap[realIconName].toString();
      bunildId = (iconMap[chunkKey] as string)?.split('.')?.[0];
      path = ICONCHUNK_PATH_PREFIX + iconMap[chunkKey];
    } else if (Number(verinfo) >= 1) {
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      bunildId = (iconMap[iconName] as string)?.split('.')?.[0];
      path = ICONCHUNK_PATH_PREFIX + iconMap[iconName];
    } else {
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      bunildId = (iconMap![iconName] as IconMapChunkType)?.chunkName || '';
      path = (iconMap![iconName] as IconMapChunkType)?.chunk || '';
    }

    if (!bunildId || !path) {
      return Promise.reject();
    }

    if (!iconChunkMap[bunildId]) {
      iconChunkMap[bunildId] = await loadJSONJS({
        domain: resourceDomain || '',
        bundleId: bunildId,
        path: path
      }) as any;
    }

    const svgContent = iconChunkMap[bunildId]?.[iconName];

    if (svgContent) {
      return Promise.resolve(svgContent);
    } else {
      return Promise.reject();
    }
  } catch (error) {
    console.warn(error);
    return Promise.reject(error);
  }
}

export const loadIconChunk = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>) => {
  const isIML = isIconMapLoaded(), listState = getListState(), symbolArea = getSymbolArea();
  return new Promise<void>(async (resolve, reject) => {
    if (!(iconName in listState) || listState[iconName] === 'loading') {
      eventEmitter.once(uiAppName, iconName, (res: boolean) => {
        if (res) {
          resolve();
        } else {
          reject();
        }
      });
    } else if (iconName in listState) {
      if (listState[iconName]) {
        resolve();
      } else {
        reject();
      }
    }

    if (!(iconName in listState)) {
      try {
        listState[iconName] = 'loading';
        if (typeof symbolArea === 'undefined') {
          let symbolAreaRes;
          if (ua.browser === 'IE') {
            symbolAreaRes = document.createElement('div');
            symbolAreaRes.id = "_WEAPP_UI_ICON_SVG_BOX";
            symbolAreaRes.style.position = 'absolute';
            symbolAreaRes.style.width = '0';
            symbolAreaRes.style.height = '0';
            document.getElementById('root')?.appendChild(symbolAreaRes);
          } else {
            symbolAreaRes = null;
            document.body.insertAdjacentHTML('afterbegin', svgElement);
            symbolAreaRes = document.getElementById('_WEAPP_UI_ICON_SVG_REFS_NODE_')?.getElementsByTagName('defs')[0];
          }
          setSymbolArea(symbolAreaRes);
        }

        if (!isIML) {
          await loadIconMap(params, options);
        }
        const iconMap = getIconMap()

        let realIconName = iconName;
        let bunildId = '', path = '';
        const verinfo = iconMap?.['version']?.toString() || 0;

        if (Number(verinfo) >= 2) {
          realIconName = iconName.replace(/^[Ii]con-/, '');
          if (!iconMap?.[realIconName]) {
            eventEmit(iconName, false);
            return;
          }
          const chunkKey = iconMap[realIconName].toString();
          bunildId = (iconMap[chunkKey] as string)?.split('.')?.[0];
          path = ICONCHUNK_PATH_PREFIX + iconMap[chunkKey];
        } else if (Number(verinfo) >= 1) {
          if (!iconMap?.[realIconName]) {
            eventEmit(iconName, false);
            return;
          }
          bunildId = (iconMap[iconName] as string)?.split('.')?.[0];
          path = ICONCHUNK_PATH_PREFIX + iconMap[iconName];
        } else {
          if (!iconMap?.[realIconName]) {
            eventEmit(iconName, false);
            return;
          }
          bunildId = (iconMap![iconName] as IconMapChunkType)?.chunkName || '';
          path = (iconMap![iconName] as IconMapChunkType)?.chunk || '';
        }

        if (!bunildId || !path || path.indexOf('.chunk') === -1) {
          eventEmit(iconName, false);
          return;
        }

        if (!iconChunkMap[bunildId]) {
          iconChunkMap[bunildId] = await loadJSONJS({
            domain: resourceDomain || '',
            bundleId: bunildId,
            path: path
          }) as any;
        }

        const svgContent = iconChunkMap[bunildId][iconName];

        if (!svgContent) {
          eventEmit(iconName, false);
          return;
        }

        appendSymbolToRefs(iconName, svgContent, {
          onSuccess: () => {
            eventEmit(iconName, true);
          },
          onError: () => {
            eventEmit(iconName, false);
          }
        });
      } catch (error) {
        console.warn(error);
        eventEmit(iconName, false);
      }
    }
  })
}

export const loadImageIcon = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>) => {

  try {
    if (resourceDomain === null) {
      const { iconResourceDomainCache, iconVersionCache } = await getIconResourceDomain();
      resourceDomain = params?.domain || iconResourceDomainCache;
      iconVersion = iconVersionCache;
    }

    const isIML = isIconMapLoaded();
    if (!isIML) {
      await loadIconMap(params, options);
    }
    const iconMap = getIconMap();

    let realIconName = iconName;
    let path = '';
    const verinfo = iconMap?.['version']?.toString() || 0;

    if (Number(verinfo) >= 2) {
      realIconName = iconName.replace(/^[Ii]con-/, '');
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      const chunkKey = iconMap[realIconName].toString();
      path = ICONCHUNK_PATH_PREFIX + iconMap[chunkKey];
    } else if (Number(verinfo) >= 1) {
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      path = ICONCHUNK_PATH_PREFIX + iconMap[iconName];
    } else {
      if (!iconMap?.[realIconName]) {
        return Promise.reject();
      }
      path = (iconMap[iconName] as IconMapChunkType).chunk;
    }

    return Promise.resolve(resourceDomain + path);
  } catch (error) {
    console.warn(error);
    return Promise.reject(error);
  }
}